import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, Eye, EyeOff, CheckCircle } from 'lucide-react';

export default function ChangePasswordPage() {
  const { isAuthenticated, changePassword } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    const result = await changePassword(currentPassword, newPassword);
    
    if (result.success) {
      setSuccess(true);
      toast.success('Password changed successfully!');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  if (success) {
    return (
      <main className="min-h-screen pt-24 pb-12 px-6 flex items-center justify-center bg-[#050505]">
        <div className="w-full max-w-md">
          <div className="bg-[#121212] border border-zinc-800 p-8 text-center" data-testid="change-success">
            <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold uppercase text-white mb-4">
              Password Changed
            </h1>
            <p className="text-zinc-400 mb-6">
              Your password has been successfully updated.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none"
            >
              Go to Home
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-12 px-6 flex items-center justify-center bg-[#050505]">
      <div className="w-full max-w-md">
        <div className="bg-[#121212] border border-zinc-800 p-8" data-testid="change-password-form">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-cyan-400/10 border border-cyan-400/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-cyan-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold uppercase text-white mb-2">
              Change Password
            </h1>
            <p className="text-zinc-500 text-sm">
              Update your account password
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                Current Password
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                  className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 pr-12 focus:border-cyan-400"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                New Password
              </Label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 6 characters"
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                data-testid="new-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                Confirm New Password
              </Label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
              data-testid="change-password-submit"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </div>
      </div>
    </main>
  );
}
